import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonContent,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonNote,
  IonPage,
  IonSearchbar,
  IonSelect,
  IonSelectOption,
  IonText,
  useIonToast,
} from "@ionic/react";
import { informationCircleOutline, personOutline } from "ionicons/icons";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import Header from "src/components/Header";
import SolicitudRRHHCampos from "src/components/SolicitudRRHHCampos";
import {
  Catalogo,
  Cargo,
  Colaborador,
  DatosColaborador,
  PuntoServicio,
  UnidadNegocio,
  ValorCampo,
  ValorEvaluacion,
  FilaGrilla,
  SolicitudDetalle,
  ValoresFormulario,
  ValidacionSolicitudError,
  adjuntosADeclarar,
  esValorEvaluacion,
  buscarColaboradores,
  buscarPuntosServicio,
  listarCargos,
  listarUnidadesNegocio,
  crearSolicitud,
  obtenerSolicitud,
  reenviarSolicitud,
  obtenerCatalogo,
  obtenerDatosColaborador,
  prellenarDesdeColaborador,
  requiereAprobacionJefe,
  subirAdjunto,
  validarFormulario,
  valoresParaEnviar,
} from "src/services/solicitudesRRHH";

const hoyTexto = (): string => new Date().toLocaleDateString("es-PY");

/**
 * Pasa lo que devuelve EMSYS de una solicitud devuelta al formato que espera
 * el formulario.
 *
 * Dos traducciones: todo va como texto (los controles son de texto) y el
 * nombre del colaborador de cada fila de la grilla pasa a la clave con la que
 * la hoja lo muestra sin volver a consultar.
 */
const valoresConEtiquetas = (solicitud: SolicitudDetalle): ValoresFormulario => {
  const crudos = (solicitud.valores || {}) as Record<string, unknown>;
  const salida: ValoresFormulario = {};

  Object.keys(crudos).forEach((codigo) => {
    const valor = crudos[codigo];
    if (Array.isArray(valor)) {
      salida[codigo] = valor.map((fila) => {
        const limpia: FilaGrilla = {};
        const celdas = (fila || {}) as Record<string, unknown>;
        Object.keys(celdas).forEach((columna) => {
          const celda = celdas[columna];
          if (celda === null || celda === undefined) return;
          const clave =
            columna === "OPERARIO_TEXTO" ? "__etiqueta_OPERARIO" : columna;
          limpia[clave] = String(celda);
        });
        return limpia;
      });
      return;
    }
    if (typeof valor === "boolean") {
      salida[codigo] = valor;
      return;
    }
    // La evaluacion de desempeño vuelve como la mando la app: {items, textos}.
    if (esValorEvaluacion(valor as ValorCampo)) {
      salida[codigo] = valor as ValorEvaluacion;
      return;
    }
    salida[codigo] = valor === null || valor === undefined ? "" : String(valor);
  });

  return salida;
};

const SolicitudRRHHNueva: React.FC = () => {
  const history = useHistory();
  // Con :id la pantalla corrige una solicitud devuelta en vez de crear una
  // nueva. Es el mismo formulario: lo unico que cambia es de donde salen los
  // valores iniciales y a que endpoint se manda.
  const { id: idCorreccion } = useParams<{ id?: string }>();
  const corrigiendo = Boolean(idCorreccion);
  const [devuelta, setDevuelta] = useState<SolicitudDetalle | null>(null);
  const [present] = useIonToast();

  const [catalogo, setCatalogo] = useState<Catalogo | null>(null);
  const [cargandoCatalogo, setCargandoCatalogo] = useState(true);
  const [motivoCodigo, setMotivoCodigo] = useState<string>("");

  const [valores, setValores] = useState<ValoresFormulario>({});
  const [archivos, setArchivos] = useState<Record<string, File | undefined>>({});
  const [errores, setErrores] = useState<Record<string, string>>({});

  // Colaborador
  const [terminoColab, setTerminoColab] = useState("");
  const [resultadosColab, setResultadosColab] = useState<Colaborador[]>([]);
  const [buscandoColab, setBuscandoColab] = useState(false);
  const [colaborador, setColaborador] = useState<Colaborador | null>(null);
  const [datosColab, setDatosColab] = useState<DatosColaborador | null>(null);

  // Punto de servicio (cabecera)
  const [terminoPs, setTerminoPs] = useState("");
  const [resultadosPs, setResultadosPs] = useState<PuntoServicio[]>([]);
  const [ps, setPs] = useState<PuntoServicio | null>(null);

  // Puntos de servicio para los campos de tipo PS (Ubicacion, Nueva
  // Asignacion). Se precargan con los del fiscal: la busqueda sin termino
  // devuelve sus PS asignados.
  const [psCampos, setPsCampos] = useState<PuntoServicio[]>([]);

  // Unidades de negocio para las celdas de los campos TABLA. Son media
  // docena y no cambian, asi que se piden una vez al abrir la pantalla.
  const [unidadesNegocio, setUnidadesNegocio] = useState<UnidadNegocio[]>([]);

  // Cargos del DWH para los campos de tipo CARGO ("Cargo Nuevo"). Son ~100 y
  // tampoco cambian seguido: se piden una vez al abrir.
  const [cargos, setCargos] = useState<Cargo[]>([]);

  // Colaboradores encontrados para los campos de tipo OPERARIO ("Superior
  // Directo Nuevo"). Lista aparte de la del colaborador de la cabecera: son
  // dos busquedas distintas y mezclarlas confunde.
  const [colabCampos, setColabCampos] = useState<Colaborador[]>([]);

  const [enviando, setEnviando] = useState(false);

  const motivo = useMemo(
    () => catalogo?.motivos.find((m) => m.codigo === motivoCodigo),
    [catalogo, motivoCodigo],
  );

  /**
   * Los motivos con grilla llevan los colaboradores adentro (uno por fila),
   * asi que la tarjeta de colaborador de la cabecera seria redundante. Sin
   * motivo elegido se muestra: es el estado inicial.
   */
  const pideColaborador = !motivo || motivo.pide_colaborador !== false;

  /**
   * Trae la solicitud devuelta y deja el formulario como estaba cuando se
   * cargo. Los campos que viajan por id llegan con su id y su etiqueta
   * aparte, porque los selectores buscan contra el servidor y con el id solo
   * mostrarian un numero.
   */
  useEffect(() => {
    if (!idCorreccion) return;
    const cargar = async () => {
      try {
        const solicitud = await obtenerSolicitud(Number(idCorreccion));
        if (!solicitud.puede_reenviar) {
          present({
            message: "Esta solicitud no esta devuelta para corregir.",
            duration: 3500,
            color: "warning",
          });
          history.replace(`/solicitudes-rrhh/${idCorreccion}`);
          return;
        }
        setDevuelta(solicitud);
        setMotivoCodigo(solicitud.motivo);
        setValores(valoresConEtiquetas(solicitud));
      } catch (err) {
        const msg = err instanceof Error ? err.message : "No se pudo abrir la solicitud";
        present({ message: msg, duration: 3500, color: "danger" });
        history.replace("/solicitudes-rrhh");
      }
    };
    void cargar();
  }, [idCorreccion, history, present]);

  useEffect(() => {
    const cargar = async () => {
      try {
        setCatalogo(await obtenerCatalogo());
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Error al cargar el catalogo";
        present({ message: msg, duration: 3500, color: "danger" });
      } finally {
        setCargandoCatalogo(false);
      }
      // Busqueda vacia: EMSYS devuelve los PS asignados al fiscal.
      try {
        setPsCampos(await buscarPuntosServicio());
      } catch {
        setPsCampos([]);
      }
      try {
        setUnidadesNegocio(await listarUnidadesNegocio());
      } catch {
        setUnidadesNegocio([]);
      }
      try {
        setCargos(await listarCargos());
      } catch {
        // El DWH es best-effort: el campo avisa que la lista no se pudo leer.
        setCargos([]);
      }
    };
    void cargar();
  }, [present]);

  /**
   * Busca colaboradores activos para los campos de tipo OPERARIO. Suma a la
   * lista en vez de reemplazarla, para no perder al que ya estaba elegido.
   */
  const buscarColabCampos = useCallback(async (termino: string) => {
    if (termino.trim().length < 2) return;
    try {
      const encontrados = await buscarColaboradores(termino.trim());
      setColabCampos((prev) => {
        const vistos = new Set(prev.map((c) => c.operario_id));
        return [...prev, ...encontrados.filter((c) => !vistos.has(c.operario_id))];
      });
    } catch {
      // Sin resultados nuevos: lo que ya estaba sigue disponible.
    }
  }, []);

  /**
   * Busca otros PS para los campos de tipo PS y los suma a la lista, sin
   * perder los del fiscal ni el que ya estaba elegido.
   */
  const buscarPSCampos = useCallback(async (termino: string) => {
    if (termino.trim().length < 2) return;
    try {
      const encontrados = await buscarPuntosServicio(termino.trim());
      setPsCampos((prev) => {
        const vistos = new Set(prev.map((p) => p.ps_id));
        return [...prev, ...encontrados.filter((p) => !vistos.has(p.ps_id))];
      });
    } catch {
      // Sin resultados nuevos: la lista del fiscal sigue disponible.
    }
  }, []);

  // Cambiar de motivo descarta lo cargado: los campos son otros.
  const elegirMotivo = (codigo: string) => {
    setMotivoCodigo(codigo);
    setValores({});
    setArchivos({});
    setErrores({});
  };

  /**
   * Deja en las listas de opciones lo que la solicitud ya tenia elegido.
   *
   * Los selectores de PS, colaborador y cargo muestran el titulo de la opcion
   * que encuentran por id; si el id no esta en la lista, el campo apareceria
   * vacio como si no se hubiera elegido nada. Aca se agrega con la etiqueta
   * congelada que manda EMSYS.
   */
  useEffect(() => {
    if (!devuelta || !motivo) return;
    const guardados = (devuelta.valores || {}) as Record<string, unknown>;
    const etiquetas = devuelta.etiquetas || {};

    motivo.campos.forEach((campo) => {
      const id = guardados[campo.codigo];
      if (id === null || id === undefined || String(id) === "") return;
      const texto = etiquetas[campo.codigo] || String(id);

      if (campo.tipo === "PS") {
        setPsCampos((prev) =>
          prev.some((p) => String(p.ps_id) === String(id))
            ? prev
            : [...prev, { ps_id: Number(id), codigo: "", nombre: texto }],
        );
      } else if (campo.tipo === "OPERARIO") {
        setColabCampos((prev) =>
          prev.some((c) => String(c.operario_id) === String(id))
            ? prev
            : [
                ...prev,
                {
                  operario_id: Number(id),
                  nombre: texto,
                  legajo: "",
                  cedula: "",
                  texto,
                },
              ],
        );
      } else if (campo.tipo === "CARGO") {
        setCargos((prev) =>
          prev.some((c) => String(c.cargo_id) === String(id))
            ? prev
            : [...prev, { cargo_id: Number(id), nombre: texto }],
        );
      }
    });
  }, [devuelta, motivo]);

  const onCambio = (codigo: string, valor: ValorCampo) => {
    setValores((prev) => {
      const siguiente = { ...prev, [codigo]: valor };
      // El Fiscal sale del punto de servicio elegido (Contrato a Plazos): lo
      // trae el mismo endpoint que los PS. Se pisa siempre, porque al cambiar
      // de punto de servicio el fiscal anterior ya no corresponde. El campo
      // queda editable: hay puntos sin fiscal asignado.
      const campo = motivo?.campos.find((c) => c.codigo === codigo);
      const tieneFiscal = motivo?.campos.some((c) => c.codigo === "FISCAL");
      if (campo?.tipo === "PS" && tieneFiscal) {
        const elegido = psCampos.find((p) => String(p.ps_id) === String(valor));
        siguiente.FISCAL = elegido?.fiscal || "";
      }
      return siguiente;
    });
    setErrores((prev) => {
      if (!prev[codigo]) return prev;
      const siguiente = { ...prev };
      delete siguiente[codigo];
      return siguiente;
    });
  };

  const onArchivo = (codigo: string, archivo: File | undefined) => {
    setArchivos((prev) => ({ ...prev, [codigo]: archivo }));
    setErrores((prev) => {
      if (!prev[codigo]) return prev;
      const siguiente = { ...prev };
      delete siguiente[codigo];
      return siguiente;
    });
  };

  const buscarColab = useCallback(
    async (term: string) => {
      if (term.trim().length < 2) {
        setResultadosColab([]);
        return;
      }
      setBuscandoColab(true);
      try {
        setResultadosColab(await buscarColaboradores(term.trim()));
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Error al buscar";
        present({ message: msg, duration: 3000, color: "danger" });
      } finally {
        setBuscandoColab(false);
      }
    },
    [present],
  );

  const elegirColaborador = async (elegido: Colaborador) => {
    setColaborador(elegido);
    setResultadosColab([]);
    setTerminoColab("");
    try {
      const datos = await obtenerDatosColaborador(elegido.operario_id);
      setDatosColab(datos);
      setValores((prev) => prellenarDesdeColaborador(motivo, datos, prev));
      // Si tiene una sola asignacion vigente, se propone ese punto de servicio.
      if (datos.asignaciones.length === 1 && !ps) {
        setPs(datos.asignaciones[0]);
      }
    } catch {
      // Los datos del colaborador son de apoyo: si el DWH no responde, la
      // solicitud se puede cargar igual.
      setDatosColab(null);
    }
  };

  const buscarPs = useCallback(
    async (term: string) => {
      if (term.trim().length < 2) {
        setResultadosPs([]);
        return;
      }
      try {
        setResultadosPs(await buscarPuntosServicio(term.trim()));
      } catch {
        setResultadosPs([]);
      }
    },
    [],
  );

  const pasaPorJefe = motivoCodigo ? requiereAprobacionJefe(motivoCodigo, valores) : false;

  const confirmar = async () => {
    if (!motivo) {
      present({ message: "Elegi el motivo de la solicitud.", duration: 2500, color: "warning" });
      return;
    }

    // Al corregir el colaborador no se cambia: quedo fijado al cargar la
    // solicitud, igual que en EMSYS web.
    if (!corrigiendo && pideColaborador && !colaborador) {
      present({
        message: "Elegi el colaborador de la solicitud.",
        duration: 2500,
        color: "warning",
      });
      return;
    }

    const encontrados = validarFormulario(motivo, valores, archivos);
    if (Object.keys(encontrados).length > 0) {
      setErrores(encontrados);
      present({
        message: "Faltan datos obligatorios.",
        duration: 2500,
        color: "warning",
      });
      return;
    }

    setEnviando(true);
    try {
      const solicitud = corrigiendo
        ? await reenviarSolicitud(Number(idCorreccion), {
            valores: valoresParaEnviar(motivo, valores),
            adjuntos_a_subir: adjuntosADeclarar(motivo, valores, archivos),
          })
        : await crearSolicitud({
            motivo: motivo.codigo,
            // Si la tarjeta no se mostro, no se manda nada de la cabecera.
            operario_id: pideColaborador ? colaborador?.operario_id : undefined,
            punto_servicio_id: pideColaborador ? ps?.ps_id : undefined,
            valores: valoresParaEnviar(motivo, valores),
            adjuntos_a_subir: adjuntosADeclarar(motivo, valores, archivos),
          });

      // Los adjuntos van en un segundo paso, con la solicitud ya creada.
      for (const codigo of adjuntosADeclarar(motivo, valores, archivos)) {
        const archivo = archivos[codigo];
        if (!archivo) continue;
        try {
          await subirAdjunto(solicitud.id, codigo, archivo);
        } catch (err) {
          const msg = err instanceof Error ? err.message : "Error subiendo archivo";
          present({ message: `${archivo.name}: ${msg}`, duration: 3500, color: "warning" });
        }
      }

      present({
        message: corrigiendo
          ? "Solicitud corregida y reenviada."
          : pasaPorJefe
            ? "Solicitud enviada a tu jefe para aprobacion."
            : "Solicitud enviada a RRHH.",
        duration: 3000,
        color: "success",
      });
      history.replace(`/solicitudes-rrhh/${solicitud.id}`);
    } catch (err) {
      if (err instanceof ValidacionSolicitudError) {
        setErrores(err.errores);
        present({ message: err.message, duration: 3500, color: "danger" });
      } else {
        const msg =
          err instanceof Error
            ? err.message
            : corrigiendo
              ? "No se pudo reenviar la solicitud"
              : "No se pudo crear la solicitud";
        present({ message: msg, duration: 3500, color: "danger" });
      }
    } finally {
      setEnviando(false);
    }
  };

  return (
    <IonPage>
      <Header
        titulo={corrigiendo ? `Corregir solicitud #${idCorreccion}` : "Nueva solicitud a RRHH"}
      />
      <IonContent>
        <IonLoading isOpen={cargandoCatalogo || enviando} message="Cargando..." />

        {/* Que hay que corregir, arriba de todo: es lo primero que
            necesita leer quien abre la pantalla. */}
        {devuelta && (
          <IonCard color="warning">
            <IonCardHeader>
              <IonCardSubtitle>
                {devuelta.devuelta_por || "Devuelta"} la devolvio para corregir
              </IonCardSubtitle>
            </IonCardHeader>
            <IonCardContent>
              <p style={{ margin: 0 }}>{devuelta.comentario_devolucion}</p>
            </IonCardContent>
          </IonCard>
        )}

        {/* Cabecera: lo que en Flokzu era de solo lectura */}
        <IonCard>
          <IonCardContent>
            <IonList lines="full">
              <IonItem lines="none">
                <IonLabel position="stacked">Fecha de registro</IonLabel>
                <IonInput
                  value={devuelta ? devuelta.fecha_registro : hoyTexto()}
                  readonly
                />
              </IonItem>
              <IonItem lines="none">
                <IonLabel position="stacked">
                  Motivo de la solicitud {corrigiendo ? "" : "*"}
                </IonLabel>
                {/* Al corregir el motivo no se cambia: los campos serian
                    otros y ya no seria la misma solicitud. */}
                {corrigiendo ? (
                  <IonInput value={motivo?.etiqueta || ""} readonly />
                ) : (
                  <IonSelect
                    value={motivoCodigo}
                    placeholder="Elegi la gestion que necesitas"
                    interface="action-sheet"
                    onIonChange={(e) => elegirMotivo(String(e.detail.value || ""))}
                  >
                    {(catalogo?.motivos || []).map((m) => (
                      <IonSelectOption key={m.codigo} value={m.codigo}>
                        {m.etiqueta}
                      </IonSelectOption>
                    ))}
                  </IonSelect>
                )}
              </IonItem>
            </IonList>
          </IonCardContent>
        </IonCard>

        {/* Al corregir, el colaborador de la cabecera quedo fijado al
            cargar: se muestra pero no se cambia. */}
        {corrigiendo && devuelta && devuelta.colaborador_nombre && (
          <IonCard>
            <IonCardHeader>
              <IonCardSubtitle>
                <IonIcon
                  icon={personOutline}
                  style={{ verticalAlign: "middle", marginRight: 6 }}
                />
                Colaborador
              </IonCardSubtitle>
            </IonCardHeader>
            <IonCardContent>
              <IonItem lines="none">
                <IonLabel>
                  <h2>{devuelta.colaborador_nombre}</h2>
                  <p>CI {devuelta.colaborador_cedula || "-"}</p>
                </IonLabel>
              </IonItem>
            </IonCardContent>
          </IonCard>
        )}

        {/* Colaborador y Punto de Servicio: no van en los motivos que llevan
            los colaboradores en la grilla, con su propia unidad de negocio. */}
        {!corrigiendo && pideColaborador && (
        <>
        <IonCard>
          <IonCardHeader>
            <IonCardSubtitle>
              <IonIcon icon={personOutline} style={{ verticalAlign: "middle", marginRight: 6 }} />
              Colaborador <span style={{ color: "var(--ion-color-danger)" }}>*</span>
            </IonCardSubtitle>
          </IonCardHeader>
          <IonCardContent>
            {colaborador ? (
              <>
                <IonItem lines="none">
                  <IonLabel>
                    <h2>{colaborador.nombre}</h2>
                    <p>
                      Legajo {colaborador.legajo || "-"} &middot; CI {colaborador.cedula || "-"}
                    </p>
                  </IonLabel>
                  <IonButton
                    fill="clear"
                    size="small"
                    slot="end"
                    onClick={() => {
                      setColaborador(null);
                      setDatosColab(null);
                    }}
                  >
                    Cambiar
                  </IonButton>
                </IonItem>
                {datosColab && (
                  <IonList lines="none">
                    {datosColab.ciudad && (
                      <IonItem>
                        <IonLabel position="stacked">Ciudad</IonLabel>
                        <IonInput value={datosColab.ciudad} readonly />
                      </IonItem>
                    )}
                    {datosColab.direccion && (
                      <IonItem>
                        <IonLabel position="stacked">Direccion</IonLabel>
                        <IonInput value={datosColab.direccion} readonly />
                      </IonItem>
                    )}
                    {datosColab.telefono && (
                      <IonItem>
                        <IonLabel position="stacked">Telefono</IonLabel>
                        <IonInput value={datosColab.telefono} readonly />
                      </IonItem>
                    )}
                    {datosColab.fecha_ingreso && (
                      <IonItem>
                        <IonLabel position="stacked">Fecha de ingreso</IonLabel>
                        <IonInput value={datosColab.fecha_ingreso} readonly />
                      </IonItem>
                    )}
                  </IonList>
                )}
              </>
            ) : (
              <>
                <IonSearchbar
                  value={terminoColab}
                  placeholder="Buscar por legajo, cedula o nombre"
                  debounce={400}
                  onIonChange={(e) => {
                    const term = e.detail.value || "";
                    setTerminoColab(term);
                    void buscarColab(term);
                  }}
                />
                {buscandoColab && (
                  <IonText color="medium">
                    <p style={{ margin: "0 0 0 1rem" }}>Buscando...</p>
                  </IonText>
                )}
                <IonList>
                  {resultadosColab.map((c) => (
                    <IonItem key={c.operario_id} button onClick={() => void elegirColaborador(c)}>
                      <IonLabel>
                        <h3>{c.nombre}</h3>
                        <p>
                          Legajo {c.legajo || "-"} &middot; CI {c.cedula || "-"}
                        </p>
                      </IonLabel>
                    </IonItem>
                  ))}
                </IonList>
              </>
            )}
          </IonCardContent>
        </IonCard>

        {/* Punto de servicio */}
        <IonCard>
          <IonCardHeader>
            <IonCardSubtitle>Punto de servicio</IonCardSubtitle>
          </IonCardHeader>
          <IonCardContent>
            {ps ? (
              <IonItem lines="none">
                <IonLabel>
                  <h3>{ps.nombre}</h3>
                  <p>{ps.codigo}</p>
                </IonLabel>
                <IonButton fill="clear" size="small" slot="end" onClick={() => setPs(null)}>
                  Cambiar
                </IonButton>
              </IonItem>
            ) : (
              <>
                {datosColab && datosColab.asignaciones.length > 1 && (
                  <IonList>
                    <IonNote style={{ paddingLeft: "1rem" }}>
                      Asignaciones vigentes del colaborador
                    </IonNote>
                    {datosColab.asignaciones.map((a) => (
                      <IonItem key={a.ps_id} button onClick={() => setPs(a)}>
                        <IonLabel>
                          <h3>{a.nombre}</h3>
                          <p>{a.codigo}</p>
                        </IonLabel>
                      </IonItem>
                    ))}
                  </IonList>
                )}
                <IonSearchbar
                  value={terminoPs}
                  placeholder="Buscar otro punto de servicio"
                  debounce={400}
                  onIonChange={(e) => {
                    const term = e.detail.value || "";
                    setTerminoPs(term);
                    void buscarPs(term);
                  }}
                />
                <IonList>
                  {resultadosPs.map((p) => (
                    <IonItem key={p.ps_id} button onClick={() => setPs(p)}>
                      <IonLabel>
                        <h3>{p.nombre}</h3>
                        <p>{p.codigo}</p>
                      </IonLabel>
                    </IonItem>
                  ))}
                </IonList>
              </>
            )}
          </IonCardContent>
        </IonCard>
        </>
        )}

        {/* Campos variables del motivo */}
        {motivo && (
          <IonCard>
            <IonCardHeader>
              <IonCardSubtitle>{motivo.etiqueta}</IonCardSubtitle>
            </IonCardHeader>
            <IonCardContent>
              <IonList lines="full">
                <SolicitudRRHHCampos
                  motivo={motivo}
                  valores={valores}
                  archivos={archivos}
                  errores={errores}
                  puntosServicio={psCampos}
                  cargos={cargos}
                  colaboradores={colabCampos}
                  onBuscarColaborador={(t) => void buscarColabCampos(t)}
                  unidadesNegocio={unidadesNegocio}
                  onCambio={onCambio}
                  onArchivo={onArchivo}
                  onBuscarPS={(t) => void buscarPSCampos(t)}
                />
              </IonList>

              {pasaPorJefe && (
                <IonText color="warning">
                  <p style={{ marginTop: "1rem" }}>
                    <IonIcon
                      icon={informationCircleOutline}
                      style={{ verticalAlign: "middle", marginRight: 6 }}
                    />
                    Esta solicitud requiere la aprobacion de tu jefe directo antes de
                    llegar a RRHH.
                  </p>
                </IonText>
              )}
            </IonCardContent>
          </IonCard>
        )}

        <div style={{ padding: "0 1rem 2rem" }}>
          <IonButton expand="block" disabled={!motivo || enviando} onClick={confirmar}>
            {corrigiendo ? "Reenviar corregida" : "Enviar solicitud"}
          </IonButton>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default SolicitudRRHHNueva;
